import { isAxiosError } from 'axios';
import { produce } from 'immer';
import { isEmpty, toLonLat } from 'components/utils';
import mapStore from 'store/map';
import filtersStore from 'store/filters';
import measurementsStore from 'store/measurements';
import { uploadFileApi } from 'store/common/api';
import { getProjectApi, createProjectApi, editProjectApi, deleteProjectApi, addToFavoriteApi, deleteFromFavoriteApi, updateRealUnitsCheckDateApi, updateTypicalUnitsCheckDateApi, resetUnitsPriceHistoryApi, sendPhoneNumberViewAnalyticsApi, sendProjectViewMetricsApi, sendProjectContactMetricsApi, syncProjectsWithDldAdminApi, getDldProjectNumbersApi, getMapProjectsApi, getListProjectsApi, getShortProjectsApi, } from '../api';
const toProjectApiError = (error) => {
    const data = isAxiosError(error)
        ? error.response?.data
        : undefined;
    if (!data?.errors?.file || data.errors.logo)
        return data;
    return produce(data, (draft) => {
        if (!draft.errors)
            return;
        draft.errors.logo = draft.errors.file;
    });
};
const getProjectWritePayload = async (project) => {
    // logo / hasOwnLogo — GET; запись через logoId
    const { logo, logoId, ...projectData } = project;
    delete projectData.hasOwnLogo;
    if (logo && typeof logo !== 'string' && logo.file) {
        const form = new FormData();
        form.append('file', logo.file);
        const { data } = await uploadFileApi(form);
        return { ...projectData, logoId: data.fileId };
    }
    // Reset кастомного логотипа → вернуться к seller
    if (logoId === null) {
        return { ...projectData, logoId: null };
    }
    return projectData;
};
const projectsActions = {
    setSelected: async (payload, { setState }) => {
        setState((state) => {
            if (state.selected?.id !== payload?.id) {
                // TODO:GM-1806 избавиться от испльзования selectedProject
                // @ts-ignore
                state.selected = payload ?? null;
                state.project = {
                    data: null,
                    units: [],
                };
            }
        });
    },
    addProject: async (payload, { setState }) => {
        setState((state) => {
            // TODO:GM-1806 создавать проект в отдельном слое
            // @ts-ignore
            state.project = { ...payload };
        });
    },
    removeProject: async (payload, { setState }) => {
        setState((state) => {
            // TODO:GM-1806 удалять проект из отдельного слоя
            // @ts-ignore
            state.project = null;
        });
    },
    getMapProjects: async (params, { signal, setState }) => {
        try {
            const filters = filtersStore.getState().filtersData;
            const { viewportPolygon, resolution } = mapStore.getState();
            const { priceMeasurement, squareMeasurement } = measurementsStore.getState();
            const payload = {
                ...filters,
                resolution,
                viewportPolygon,
                areaUnit: squareMeasurement,
                currencyCode: priceMeasurement,
            };
            // @ts-ignore
            const { data: res } = await getMapProjectsApi({ ...payload, ...params }, signal);
            setState((state) => {
                state.data = res;
            });
            return res;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    getListProjects: async (params, { signal, setState }) => {
        try {
            const filters = filtersStore.getState().filtersData;
            const { viewportPolygon } = mapStore.getState();
            const { priceMeasurement, squareMeasurement } = measurementsStore.getState();
            const payload = {
                ...filters,
                viewportPolygon,
                areaUnit: squareMeasurement,
                currencyCode: priceMeasurement,
            };
            const { data: res } = await getListProjectsApi({ ...payload, ...params }, signal);
            setState((state) => {
                state.data.list = res.list;
                state.data.projects = res.projects;
            });
            return res;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    getShortProjects: async (payload, { signal }) => {
        try {
            const { data: res } = await getShortProjectsApi(payload, signal);
            return res;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    getProject: async ({ projectId }, { signal, setState }) => {
        try {
            const { data: res } = await getProjectApi(projectId, signal);
            setState((state) => {
                res.finishingIds = res?.finishes?.map(({ id }) => id);
                state.project.data = res;
            });
            return {
                ...res,
                finishingIds: res?.finishes?.map(({ id }) => id),
                geometry: toLonLat(res.geometry).reverse().join(', '),
            };
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    createProject: async (project) => {
        try {
            const payload = await getProjectWritePayload(project);
            const { data } = await createProjectApi(payload);
            return data;
        }
        catch (error) {
            throw toProjectApiError(error);
        }
    },
    editProject: async (project) => {
        try {
            const payload = await getProjectWritePayload(project);
            const { data } = await editProjectApi(payload);
            return data;
        }
        catch (error) {
            throw toProjectApiError(error);
        }
    },
    deleteProject: async (project, { setState }) => {
        try {
            const { data: res } = await deleteProjectApi(project);
            setState(({ data }) => {
                data.projects = data.projects.filter(({ id }) => id !== Number(res.id));
                data.features.projects = data.features.projects.filter(({ id }) => id !== Number(res.id));
            });
            return res;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    addToFavorite: async (id, { setState }) => {
        try {
            setState((state) => {
                // Меняем отображение в списке проектов
                const projectInList = state.data.projects.find((item) => item.id === id);
                if (projectInList)
                    projectInList.isFavorite = true;
                // Меняем отображение на карте
                const projectInMap = state.data.features.projects.find((item) => item.id === id);
                if (projectInMap)
                    projectInMap.isFavorite = true;
                // Меняем отображение в карточке проекта
                if (!isEmpty(state?.project?.data)) {
                    state.project.data.isFavorite = true;
                }
            });
            await addToFavoriteApi(id);
            return id;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    deleteFromFavorite: async (id, { setState }) => {
        try {
            setState((state) => {
                // Меняем отображение в списке проектов
                const projectInList = state.data.projects.find((item) => item.id === id);
                if (projectInList)
                    projectInList.isFavorite = false;
                // Меняем отображение на карте
                const projectInMap = state.data.features.projects.find((item) => item.id === id);
                if (projectInMap)
                    projectInMap.isFavorite = false;
                // Меняем отображение в карточке проекта
                if (!isEmpty(state?.project?.data)) {
                    state.project.data.isFavorite = false;
                }
            });
            await deleteFromFavoriteApi(id);
            return id;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    updateRealUnitsCheckDate: async (id) => {
        try {
            await updateRealUnitsCheckDateApi(id);
            return id;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    resetUnitsPriceHistory: async (id) => {
        try {
            await resetUnitsPriceHistoryApi(id);
            return id;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    updateTypicalUnitsCheckDate: async (id) => {
        try {
            await updateTypicalUnitsCheckDateApi(id);
            return id;
        }
        catch (error) {
            // @ts-ignore
            throw error?.response?.data;
        }
    },
    sendPhoneNumberViewAnalytics: async (projectId) => {
        try {
            await sendPhoneNumberViewAnalyticsApi(projectId);
            return projectId;
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    sendProjectViewMetrics: async (projectId) => {
        try {
            await sendProjectViewMetricsApi(projectId);
        }
        catch {
            // fire-and-forget: ошибка не должна попадать в UI
        }
        return projectId;
    },
    sendProjectContactMetrics: async (projectId) => {
        try {
            await sendProjectContactMetricsApi(projectId);
        }
        catch {
            // fire-and-forget: ошибка не должна попадать в UI
        }
        return projectId;
    },
    syncProjectsWithDld: async ({ projectId, data }) => {
        try {
            const { data: res } = await syncProjectsWithDldAdminApi(projectId, data);
            return res;
        }
        catch (err) {
            const error = err;
            throw error.response?.data;
        }
    },
    getDldProjectNumbers: async (projectId) => {
        try {
            const { data } = await getDldProjectNumbersApi(projectId);
            return data.dldProjectNumbers;
        }
        catch (err) {
            const error = err;
            if (error.response?.status === 404) {
                return [];
            }
            throw error.response?.data;
        }
    },
};
export default projectsActions;
